// 콤보 길잡이(2026-10-27 대표님 "스킬 포인트에 따라 뭘 쓸 수 있고, 콤보를 어떻게 쓰는지가 잘 이어지면 좋겠다").
// 지금 장착한(배운) 스킬의 실제 데이터(skills/<hero>.ts: 몸 이동·당김·장판·props.launcher·downChase)와 실제 연계 규칙으로 추천 연계를 그림 한 줄씩 만든다.
//  - 몰이 연계(LINK): 서로 다른 스킬을 끊김 없이(캔슬 구간 0.4~0.6, LINK.window 안) 이으면 1→2→3단, 스킬 피해 ×1 → ×1.2 → ×1.35(config LINK.damage).
//    같은 스킬 연속은 거부된다(COMBAT-FEEL 2.8). 순서: 파고들기(돌진) → 모으기(당김·회오리·덫) → 마무리(도약·큰 장판). 카엘 기본은 돌진 → 폭풍 → 낙화(문서 정본).
//  - 띄우기 연계: 런처(props.launcher) → 공중 추가타(기본 공격 J, 2.2절) → 다운 추격(props.downChase 스킬 60%, 2.7절) 또는 마무리.
// 슬롯 키 번호(지금 쪽 1~6, 다른 쪽 Alt+1~6)를 같이 준다. 순수 함수(DOM 없음) — 시험이 규칙을 고정한다.
import { LINK } from '../shared/config';
import type { SkillTimeline } from '../shared/skills/types';
import { SLOTS_PER_PAGE, SLOT_COUNT, type SlotsView } from '../shared/skillSlots';

export type ComboRole = 'gap' | 'gather' | 'launch' | 'finish' | 'hit';
export interface ComboStep {
  /** 0 = 기본 공격(J) */
  readonly skill: number;
  /** 화면에 붙일 키: '1'..'6'(지금 쪽), 'A1'..'A6'(다른 쪽 Alt), 기본 공격 'J' */
  readonly key: string;
  readonly role: ComboRole | 'air';
  /** 이 단계까지의 LINK 단계(스킬만, 기본 공격은 0) */
  readonly link: number;
}
export interface ComboLine {
  readonly kind: 'link' | 'air';
  readonly steps: readonly ComboStep[];
  /** 마지막 LINK 단계와 피해 배율(몰이 연계) */
  readonly linkStage: number;
  readonly linkMult: number;
}
export interface ComboGuide {
  readonly lines: readonly ComboLine[];
  /** 연계를 만들 스킬이 모자라면 몇 개 더 장착해야 하는지(0이면 충분) */
  readonly needMore: number;
}

/** 스킬 하나의 역할(데이터에서 읽는다) */
export function comboRole(t: SkillTimeline): ComboRole {
  if (t.props.launcher) return 'launch';
  if (t.motion?.kind === 'jump') return 'finish';
  const zones = t.hitboxes.map((h) => (h.shape.kind === 'zone' ? h.shape.zone : null));
  // 영웅 발밑 큰 장판(하늘 낙하 등)은 마무리
  if (zones.some((z) => z && z.at === 'hero' && z.kind === 'rain')) return 'finish';
  if (t.pull || zones.some((z) => z && (z.kind === 'vortex' || z.kind === 'net'))) return 'gather';
  if (t.hitboxes.some((h) => h.shape.kind === 'arrow' && h.shape.arrow.vortex)) return 'gather';
  if (t.motion?.kind === 'dash' && t.motion.distance > 0) return 'gap';
  return 'hit';
}

const ORDER: Readonly<Record<ComboRole, number>> = { gap: 0, gather: 1, hit: 2, launch: 2, finish: 3 };

/** 장착 칸의 키 표시: 지금 쪽이면 1..6, 다른 쪽이면 A1..A6 */
export function slotKey(slot: number, page: number): string {
  const n = (slot % SLOTS_PER_PAGE) + 1;
  return Math.floor(slot / SLOTS_PER_PAGE) === page ? String(n) : 'A' + n;
}

/**
 * skills = 그 영웅 스킬 표(heroSkills), learned = 배운 스킬 비트마스크(1 << (id-1)), slots = 장착 칸.
 * 장착했고 배운 스킬만 쓴다(지금 쪽을 먼저). 몰이 연계는 서로 다른 스킬 최대 LINK.maxStage개, 띄우기 연계는 런처가 있을 때만.
 */
export function comboGuide(skills: readonly SkillTimeline[], learned: number, slots: SlotsView): ComboGuide {
  const eq: { t: SkillTimeline; slot: number; role: ComboRole }[] = [];
  const seen = new Set<number>();
  // 지금 쪽 → 다른 쪽 순서로 훑어 같은 스킬이 두 칸에 있으면 지금 쪽 칸을 쓴다
  for (let k = 0; k < SLOT_COUNT; k++) {
    const slot = (slots.page * SLOTS_PER_PAGE + k) % SLOT_COUNT;
    const id = slots.slots[slot] | 0;
    if (id <= 0 || seen.has(id) || (learned & (1 << (id - 1))) === 0) continue;
    const t = skills.find((s) => s.id === id);
    if (!t) continue;
    seen.add(id);
    eq.push({ t, slot, role: comboRole(t) });
  }
  const lines: ComboLine[] = [];
  const max = LINK.maxStage;
  // 몰이 연계: 런처는 띄우기 줄에서 쓰므로 다른 스킬이 충분하면 뺀다
  const pool = eq.filter((e) => e.role !== 'launch').length >= 2 ? eq.filter((e) => e.role !== 'launch') : eq;
  const order = pool.slice().sort((a, b) => ORDER[a.role] - ORDER[b.role] || a.t.cooldown - b.t.cooldown || a.slot - b.slot);
  if (order.length >= 2) {
    // 마무리가 있으면 맨 끝에 두고 앞을 채운다(파고들기 → 모으기 → 마무리)
    const finish = order.filter((e) => e.role === 'finish');
    const head = order.filter((e) => e.role !== 'finish');
    const pick = finish.length ? [...head.slice(0, max - 1), finish[0]] : head.slice(0, max);
    const steps = pick.map((e, i) => ({ skill: e.t.id, key: slotKey(e.slot, slots.page), role: e.role, link: i + 1 }));
    const stage = steps.length;
    lines.push({ kind: 'link', steps, linkStage: stage, linkMult: LINK.damage[Math.min(stage, LINK.damage.length) - 1] });
  }
  const launcher = eq.find((e) => e.role === 'launch');
  if (launcher) {
    const rest = eq.filter((e) => e !== launcher);
    const down = rest.find((e) => e.t.props.downChase) ?? rest.find((e) => e.role === 'finish') ?? rest[0];
    const steps: ComboStep[] = [
      { skill: launcher.t.id, key: slotKey(launcher.slot, slots.page), role: 'launch', link: 1 },
      { skill: 0, key: 'J', role: 'air', link: 0 },
    ];
    if (down) steps.push({ skill: down.t.id, key: slotKey(down.slot, slots.page), role: down.role === 'finish' ? 'finish' : 'hit', link: 2 });
    const stage = down ? 2 : 1;
    lines.push({ kind: 'air', steps, linkStage: stage, linkMult: LINK.damage[stage - 1] });
  }
  return { lines, needMore: Math.max(0, 2 - eq.length) };
}
